import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  BriefcaseBusiness,
  CalendarClock,
  ClipboardCheck,
  FileText,
  Gavel,
  LayoutDashboard,
  Loader2,
  Plus,
  RefreshCcw,
  Search,
  Send,
  ShieldCheck,
  Trash2
} from "lucide-react";
import "./styles.css";

const CATEGORIES = [
  { key: "tasks", label: "工作任务", icon: ClipboardCheck },
  { key: "contracts", label: "合同风险", icon: BriefcaseBusiness },
  { key: "cases", label: "案件纠纷", icon: Gavel },
  { key: "rectifications", label: "整改督办", icon: AlertTriangle },
  { key: "policies", label: "制度知识库", icon: BookOpen },
  { key: "docs", label: "材料库", icon: FileText }
];

const STATUS = ["未开始", "进行中", "待反馈", "已完成", "已逾期", "暂停"];
const PRIORITIES = ["低", "中", "高", "紧急"];
const RISKS = ["低", "中", "高", "重大"];

const emptyForm = {
  category: "tasks",
  title: "",
  source: "",
  department: "",
  owner: "丁伟",
  collaborator: "",
  due_date: "",
  status: "进行中",
  priority: "中",
  risk: "低",
  next_action: "",
  summary: "",
  link: "",
  amount: "",
  stage: "",
  tags: ""
};

async function api(path, options) {
  const res = await fetch(path, {
    headers: { "content-type": "application/json" },
    ...options
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "请求失败");
  return data;
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <section className={`stat ${tone || ""}`}>
      <Icon size={20} />
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </section>
  );
}

function Pill({ children, type }) {
  return <span className={`pill ${type || ""}`}>{children}</span>;
}

function RecordForm({ form, setForm, onSubmit, busy }) {
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <form className="form" onSubmit={onSubmit}>
      <div className="form-grid">
        <label>
          类型
          <select value={form.category} onChange={(e) => set("category", e.target.value)}>
            {CATEGORIES.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className="wide">
          标题
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="输入事项、合同、案件、整改或材料名称" />
        </label>
        <label>
          来源
          <input value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="领导交办/会议纪要/台账导入" />
        </label>
        <label>
          责任部门
          <input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="综合部" />
        </label>
        <label>
          责任人
          <input value={form.owner} onChange={(e) => set("owner", e.target.value)} />
        </label>
        <label>
          截止时间
          <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
        </label>
        <label>
          状态
          <select value={form.status} onChange={(e) => set("status", e.target.value)}>
            {STATUS.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          优先级
          <select value={form.priority} onChange={(e) => set("priority", e.target.value)}>
            {PRIORITIES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          风险等级
          <select value={form.risk} onChange={(e) => set("risk", e.target.value)}>
            {RISKS.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          阶段/版本
          <input value={form.stage} onChange={(e) => set("stage", e.target.value)} placeholder="一审/V5.0/草稿" />
        </label>
        <label>
          金额/规模
          <input value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="可填区间，不填敏感明细" />
        </label>
        <label>
          标签
          <input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="合同;法务;战略" />
        </label>
        <label className="wide">
          下一步动作
          <input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="下一次最小可执行动作" />
        </label>
        <label className="wide">
          链接
          <input value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="飞书链接或文件索引，不放敏感正文" />
        </label>
        <label className="full">
          摘要
          <textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="只写脱敏摘要、进展或风险说明" rows={4} />
        </label>
      </div>
      <button className="primary" disabled={busy}>
        {busy ? <Loader2 className="spin" size={16} /> : <Plus size={16} />}
        保存记录
      </button>
    </form>
  );
}

function App() {
  const [active, setActive] = useState("tasks");
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ today: 0, overdue: 0, highRisk: 0, waiting: 0, totalOpen: 0 });
  const [focus, setFocus] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assistantType, setAssistantType] = useState("office");
  const [assistantInput, setAssistantInput] = useState("");
  const [assistantOutput, setAssistantOutput] = useState("");

  const categoryLabel = useMemo(() => CATEGORIES.find((item) => item.key === active)?.label || "工作任务", [active]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("category", active);
      if (query.trim()) params.set("q", query.trim());
      const [recordData, summaryData] = await Promise.all([
        api(`/api/records?${params.toString()}`),
        api("/api/summary")
      ]);
      setRecords(recordData.records || []);
      setSummary(summaryData.summary || summary);
      setFocus(summaryData.focus || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [active]);

  async function submitRecord(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/records", { method: "POST", body: JSON.stringify(form) });
      setForm({ ...emptyForm, category: form.category, owner: form.owner || "丁伟" });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeRecord(id) {
    setBusy(true);
    setError("");
    try {
      await api(`/api/records/${id}`, { method: "DELETE" });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function runAssistant() {
    setBusy(true);
    setError("");
    setAssistantOutput("");
    try {
      const data = await api("/api/assistant", {
        method: "POST",
        body: JSON.stringify({ type: assistantType, input: assistantInput })
      });
      setAssistantOutput(data.output || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <div>
          <div className="eyebrow"><ShieldCheck size={16} /> Cloudflare Pages + D1</div>
          <h1>丁伟工作驾驶舱</h1>
        </div>
        <button className="ghost" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="spin" size={16} /> : <RefreshCcw size={16} />}
          刷新
        </button>
      </header>

      {error && <div className="error">{error}</div>}

      <section className="stats">
        <StatCard icon={CalendarClock} label="今日待办" value={summary.today} tone="blue" />
        <StatCard icon={AlertTriangle} label="逾期事项" value={summary.overdue} tone="red" />
        <StatCard icon={ShieldCheck} label="高风险" value={summary.highRisk} tone="amber" />
        <StatCard icon={Loader2} label="待反馈" value={summary.waiting} />
        <StatCard icon={BarChart3} label="未完成总数" value={summary.totalOpen} />
      </section>

      <section className="layout">
        <aside className="sidebar">
          <div className="section-title"><LayoutDashboard size={16} /> 台账模块</div>
          {CATEGORIES.map(({ key, label, icon: Icon }) => (
            <button key={key} className={active === key ? "nav active" : "nav"} onClick={() => setActive(key)}>
              <Icon size={18} />
              {label}
            </button>
          ))}

          <div className="section-title">重点事项</div>
          <div className="focus-list">
            {focus.length === 0 && <p className="muted">暂无重点事项</p>}
            {focus.map((item) => (
              <div className="focus" key={item.id}>
                <strong>{item.title}</strong>
                <span>{item.due_date || "无截止时间"} · {item.risk}</span>
              </div>
            ))}
          </div>
        </aside>

        <section className="content">
          <div className="panel">
            <div className="panel-head">
              <h2>{categoryLabel}</h2>
              <div className="search">
                <Search size={16} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="搜索标题、摘要、标签" />
                <button onClick={load}>查询</button>
              </div>
            </div>

            <div className="records">
              {records.length === 0 && <div className="empty">暂无记录。先在下方新增一条。</div>}
              {records.map((item) => (
                <article className="record" key={item.id}>
                  <div className="record-main">
                    <h3>{item.title}</h3>
                    <p>{item.summary || "无摘要"}</p>
                    <div className="meta">
                      <span>{item.department || "未填部门"}</span>
                      <span>{item.owner || "未填责任人"}</span>
                      <span>{item.due_date || "无截止时间"}</span>
                    </div>
                  </div>
                  <div className="record-side">
                    <Pill>{item.status}</Pill>
                    <Pill type={item.risk === "高" || item.risk === "重大" ? "risk" : ""}>{item.risk}</Pill>
                    <Pill type={item.priority === "紧急" ? "urgent" : ""}>{item.priority}</Pill>
                    <button className="icon danger" title="删除" onClick={() => removeRecord(item.id)} disabled={busy}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="split">
            <section className="panel">
              <h2>新增记录</h2>
              <RecordForm form={form} setForm={setForm} onSubmit={submitRecord} busy={busy} />
            </section>

            <section className="panel assistant">
              <h2>AI草稿助手</h2>
              <label>
                类型
                <select value={assistantType} onChange={(e) => setAssistantType(e.target.value)}>
                  <option value="office">综合办公助手</option>
                  <option value="legal">法务合规助手</option>
                  <option value="policy">制度体系助手</option>
                  <option value="strategy">战略材料助手</option>
                </select>
              </label>
              <label>
                输入
                <textarea rows={7} value={assistantInput} onChange={(e) => setAssistantInput(e.target.value)} placeholder="输入脱敏摘要、会议纪要或工作要点" />
              </label>
              <button className="primary" onClick={runAssistant} disabled={busy || !assistantInput.trim()}>
                <Send size={16} />
                生成草稿
              </button>
              {assistantOutput && <pre>{assistantOutput}</pre>}
            </section>
          </div>
        </section>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")).render(<App />);
